import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import useParam from '../../context/ParamContext';
import { useLoader } from '../../provider/LoaderProvider';
import taskProdGanttService from '../../service/taskProdGantt.service';
import simpletaskService from '../../service/simpletask.service';
import { ColorPicker } from '../../config/ColorPicker';
import EventChip from './EventChip';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import ProductDialog from './ProductDialog';
import productionService from '../../service/production.service';
import useView from '../../hooks/useView';
import GanttFilter from './GanttFilter';
import SimpleTaskDialog from './SimpleTaskDialog';
import useUsers from '../../context/UsersContext';
import { generateFilter } from '../../utils/helper';
import { DrawerUI } from '../Interface';
import ProductionStore from './ProductionStore';
import useMenus from '../../context/MenuContext';
import useSaveView from '../../hooks/useSaveView';
import FloatingProductionStore from './FloatingProductionStore';
import './FloatingProductionStore.css';

export const TaskProdGanttForm = () => {
  const calendarRef = useRef(null);
  const { getUser, user } = useUsers();
  const saveView = useSaveView(user);
  const { getParam, params } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const ganttFilter = useView('ganttFilter', user);
  const checkboxFiltersRef = useRef(null);
  const { showLoader, hideLoader } = useLoader();
  const [tasks, setTasks] = useState({ resources: [], taskEvents: [], prodEvents: [], backgroundEvents: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);
  const [simpleTaskDialogOpen, setSimpleTaskDialogOpen] = useState(false);
  const [task, setTask] = useState({});
  const [production, setProduction] = useState({});
  const [dateRange, setDateRange] = useState({});
  const [taskChanged, setTaskChanged] = useState(false);
  const [productionChanged, setProductionChanged] = useState(false);
  const [filterFormState, setFilterFormState] = useState({
    productionId: null,
    operationId: null,
    reference: '',
  });
  const [query, setQuery] = useState({
    filter: [],
  });
  const [prevFilter, setPrevFilter] = useState(query.filter);
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);

  const [timeInterval, setTimeInterval] = useState({});
  const [intervalButtons, setIntervalButtons] = useState({});
  const [calendarViews, setCalendarViews] = useState({});
  const [selectedView, setSelectedView] = useState('');
  const [selectedEvents, setSelectedEvents] = useState({ id: [] });
  const [canEdit, setCanEdit] = useState(false);
  const [relatedMachines, setRelatedMachines] = useState(false);
  const [storeRevision, setStoreRevision] = useState(0);
  const [storeSaving, setStoreSaving] = useState(false);
  const [floatingStoreOpen, setFloatingStoreOpen] = useState(false);
  const [activeStoreResourceId, setActiveStoreResourceId] = useState(null);
  const floatingStoreEnabled = params.PROD_GANTT_FLOATING_STORE_ENABLED === 'Y';

  useEffect(() => {
    if (floatingStoreEnabled && user.userId) {
      setFloatingStoreOpen(user.views?.productionFloatingStore?.open !== false);
      setActiveStoreResourceId(user.views?.productionFloatingStore?.resourceId || null);
    } else {
      setFloatingStoreOpen(false);
    }
  }, [floatingStoreEnabled, user.userId, user.views?.productionFloatingStore?.open, user.views?.productionFloatingStore?.resourceId]);

  const setStoreOpen = (open) => {
    setFloatingStoreOpen(open);
    if (floatingStoreEnabled) {
      saveView('productionFloatingStore', { open }).then((saved) => {
        if (saved === false) addNotification({ content: 'A gyártástár ablakállapotának mentése sikertelen.', type: NotificationType.ERROR });
      });
    }
  };

  const refreshCalendar = () => {
    const calendarApi = calendarRef.current.getApi();
    setTimeout(() => {
      calendarApi.render();
    }, 100);
  };

  useEffect(() => {
    refreshCalendar();
  }, [tasks.taskEvents, tasks.prodEvents, tasks.backgroundEvents]);

  const slotIntervals = {
    halfHour: { hours: 0.5 },
    hour: { hours: 1 },
    day: { days: 1 },
    week: { weeks: 1 },
    month: { months: 1 },
  };

  const calendarHourViews = {
    resourceTimelineDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [
        { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [
        { day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
  };

  const calendarDayViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'long' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'short' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'short' }, { day: 'numeric' }],
    },
  };

  const calendarWeekViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
  };

  const calendarMonthViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'long' }],
    },
  };

  useEffect(() => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    setTimeInterval(slotIntervals.halfHour);
    setCalendarViews(calendarHourViews);

    // @TODO promiseArray
    getMachines();
    getParam();
    getMenus();
    if (!Object.keys(user).length) getUser();

    const toolbarElement = document.querySelector('.fc-toolbar-chunk');
    if (toolbarElement && checkboxFiltersRef.current) {
      toolbarElement.appendChild(checkboxFiltersRef.current);
    }
  }, []);

  useEffect(() => {
    const subMenuId = getMenuSubMenuId('taskProdGantt');
    setCanEdit(getCanEdit(subMenuId));
  }, [menus]);

  const conditions = {
    like: 'reference',
  };

  useEffect(() => {
    const newFilter = generateFilter(filterFormState, conditions);
    setQuery((prev) => {
      return { ...prev, filter: [...newFilter] };
    });
  }, [filterFormState]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      if (ganttFilter?.production) {
        getProductions();
      } else {
        setTasks((prevTasks) => ({
          ...prevTasks,
          prodEvents: [],
        }));
      }

      if (ganttFilter?.task) {
        getTasks();
      } else {
        setTasks((prevTasks) => ({
          ...prevTasks,
          taskEvents: [],
        }));
      }
    }
  }, [dateRange, ganttFilter]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      getActiveTimes();
    }
  }, [dateRange]);

  useEffect(() => {
    setIntervalButtons(generateButtons(selectedView));
  }, [selectedView]);

  useEffect(() => {
    if (JSON.stringify(prevFilter) === JSON.stringify(query.filter)) {
      return;
    }

    setPrevFilter(query.filter);

    if (ganttFilter?.production) {
      getProductions();
    }
  }, [query.filter, ganttFilter?.production]);

  useEffect(() => {
    if (taskChanged && ganttFilter?.task) {
      getTasks();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  useEffect(() => {
    if (productionChanged) {
      if (ganttFilter?.production) getProductions();
      setStoreRevision((value) => value + 1);
      setProductionChanged(false);
    }
  }, [productionChanged]);

  const getMachines = () => {
    showLoader();
    taskProdGanttService
      .getMachines()
      .then((data) => {
        setTasks((prevState) => ({ ...prevState, resources: data }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getActiveTimes = () => {
    //showLoader();
    taskProdGanttService
      .getActiveTimeByRange(dateRange)
      .then((data) => {
        setTasks((prevState) => ({ ...prevState, backgroundEvents: data }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const colorParam = params.TASKGANTT_CHIP_COLOR_THEME;
  const parsedParam = colorParam ? JSON.parse(colorParam) : [];

  const getProductions = () => {
    //showLoader();
    const { startDate, endDate } = dateRange;
    taskProdGanttService
      .getProductions({ startDate, endDate, query })
      .then((data) => {
        const prodEvents = data.flatMap((task) => {
          const { productionOperation, baseOrderLine } = task;
          if (!productionOperation || productionOperation.length === 0) return [];

          return productionOperation.flatMap((operation) => {
            let colorStyles = getColorStyles({ ...task, ...operation }, parsedParam[0], 'PRODUCTION');

            if (
              params.PROD_GANTT_DUEDATE_NOTIFICATION === 'Y' &&
              baseOrderLine?.doc?.dueDate &&
              dayjs(baseOrderLine.doc.dueDate).isBefore(dayjs(operation.endDate))
            ) {
              const colorObj = ColorPicker.find((color) => color.colorName === 'red');
              colorStyles = {
                backgroundColor: colorObj?.colorBgCode,
                textColor: colorObj?.textColor,
              };
            }

            const baseEvent = {
              id: operation.id,
              title: operation.operation?.operationName,
              start: operation.startDate,
              end: operation.endDate
                ? operation.endDate
                : dayjs(operation.startDate)
                    .add(
                      operation.plannedQty,
                      params.QUANTITY_UNIT_HOUR_CODE === operation.quantityUnit.code ? 'hour' : 'minute'
                    )
                    .toISOString(),
              resourceId: `M${operation.resourceOneId}`,
              ...colorStyles,
              eventData: { ...task, type: 'PRODUCTION' },
            };

            const multiResourceEvents =
              operation.productionOperationMultiResource?.map((multiResource) => ({
                ...baseEvent,
                id: multiResource.id,
                resourceId: `M${multiResource.resourceOneId}`,
                type: 'multiResource',
              })) || [];

            return [baseEvent, ...multiResourceEvents];
          });
        });
        setTasks((prevState) => ({ ...prevState, prodEvents }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getTasks = () => {
    //showLoader();
    taskProdGanttService
      .getTask(dateRange)
      .then((data) => {
        const taskEvents = data.map((task) => {
          //@TODO végül miben maradtunk? a BE adja jól vissza, vagy megtartjuk így -- megtartjuk így
          const { resourceOne, simpleTaskId, simpleTaskName, startDate, endDate, responsiblePerson } = task;
          if (!resourceOne) return null;

          const colorStyles = getColorStyles(task, parsedParam[0], 'TASK');
          return {
            id: simpleTaskId,
            title: simpleTaskName,
            start: startDate,
            responsiblePerson: responsiblePerson?.userName,
            end: endDate,
            resourceId: `M${resourceOne.id}`,
            ...colorStyles,
            eventData: { ...task, type: 'TASK' },
          };
        });
        setTasks((prevState) => ({ ...prevState, taskEvents }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getTaskById = (simpleTaskId) => {
    showLoader();
    simpletaskService
      .getTaskById(simpleTaskId)
      .then((data) => {
        setTask(data);
        setDialogOpen(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getProductById = (id) => {
    showLoader();
    productionService
      .getProductionById(id)
      .then((data) => {
        setProduction(data);
        setProductDialogOpen(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getColorStyles = (event, colorProperties, eventType) => {
    const defaultStyles = { backgroundColor: '#fff', textColor: '#000' };

    const colorProperty = colorProperties?.[eventType];
    if (!colorProperty) return defaultStyles;

    const colorName = colorProperty.split('.').reduce((obj, key) => obj?.[key], event);
    if (!colorName) return defaultStyles;

    const colorObj = ColorPicker.find((color) => color.colorName === colorName);
    return {
      backgroundColor: colorObj?.colorBgCode || defaultStyles.backgroundColor,
      textColor: colorObj?.textColor || defaultStyles.textColor,
    };
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      !startDate.isSame(dateRange.startDate) ||
      !endDate.isSame(dateRange.endDate)
    ) {
      setDateRange({ startDate, endDate });
    }

    if (selectedView !== dateInfo.view.type) setSelectedView(dateInfo.view.type);
  };

  const updateEvent = (resizeInfo) => {
    const { event, revert, newResource } = resizeInfo;
    const { eventData } = event.extendedProps;
    const resource = event.getResources()[0].id;

    const isMachine = resource.startsWith('M');

    if (!isMachine) {
      addNotification({
        content: 'Az esemény nem helyezhető csoportra!',
        type: NotificationType.ERROR,
      });
      revert();
      return;
    }

    const durationInMinutes = event.end ? dayjs(event.end).diff(dayjs(event.start), 'minute') : null;
    const duration = durationInMinutes / 60;
    const startDate = dayjs(event.start).toISOString();
    const endDate = event.end
      ? dayjs(event.end).toISOString()
      : dayjs(event.start).add(durationInMinutes, 'minute').toISOString();

    const resourceOneId = parseInt(resource.substring(1));

    const updatedEventData = {
      resourceOneId,
      startDate,
      endDate,
    };

    //showLoader();

    if (eventData.type === 'TASK') {
      const simpleTaskId = event.id;

      updatedEventData.duration = duration;
      taskProdGanttService
        .updateTask(updatedEventData, simpleTaskId)
        .then((data) => {
          addNotification({
            content: 'Sikeres módosítás!',
            type: NotificationType.SUCCESS,
          });
        })
        .catch(() => {
          revert();
        });
    } else if (eventData.type === 'PRODUCTION') {
      const id = selectedEvents.id.length > 0 ? selectedEvents.id : eventData.productionOperation[0].id;

      if (params.PRODUCTION_OPERATION_TIMESPENT_UPDATE !== 'N') {
        updatedEventData.plannedQty = duration;
      }

      if (event.extendedProps.type === 'multiResource' && !newResource) {
        taskProdGanttService
          .updateProduction({ startDate, endDate, id })
          .then((data) => {
            addNotification({
              content: 'Sikeres módosítás!',
              type: NotificationType.SUCCESS,
            });
            setProductionChanged(true);
            setSelectedEvents({ id: [] });
          })
          .catch(() => {
            revert();
          });
      } else if (event.extendedProps.type === 'multiResource' && newResource) {
        productionService
          .updateProductionOperationMultiResource(resourceOneId, parseInt(event.id))
          .then((data) => {
            addNotification({
              content: 'Sikeres módosítás!',
              type: NotificationType.SUCCESS,
            });
            setProductionChanged(true);
            setSelectedEvents({ id: [] });
          })
          .catch(() => {
            revert();
          });
        taskProdGanttService
          .updateProduction({ startDate, endDate, id })
          .then((data) => {
            addNotification({
              content: 'Sikeres módosítás!',
              type: NotificationType.SUCCESS,
            });
            setProductionChanged(true);
            setSelectedEvents({ id: [] });
          })
          .catch(() => {
            revert();
          });
      } else {
        taskProdGanttService
          .updateProduction({ ...updatedEventData, id })
          .then((data) => {
            addNotification({
              content: 'Sikeres módosítás!',
              type: NotificationType.SUCCESS,
            });
            setProductionChanged(true);
            setSelectedEvents({ id: [] });
          })
          .catch(() => {
            revert();
          });
      }
    }
  };

  const receiveStoreOperation = async (info) => {
    const resource = info.event.getResources()[0]?.id;
    if (!canEdit || storeSaving || !resource?.startsWith('M')) {
      info.revert();
      return;
    }
    setStoreSaving(true);
    try {
      await taskProdGanttService.scheduleOperationFromStore({
        id: info.event.extendedProps.operationId,
        resourceOneId: Number(resource.substring(1)),
        startDate: dayjs(info.event.start).toISOString(),
        endDate: dayjs(info.event.end).toISOString(),
      });
      info.event.remove();
      setProductionChanged(true);
      addNotification({ content: 'Sikeres tervezés!', type: NotificationType.SUCCESS });
    } catch {
      info.revert();
      setStoreRevision((value) => value + 1);
    } finally {
      setStoreSaving(false);
    }
  };

  const timeIntervalButtons = {
    halfHour: {
      text: 'Fél óra',
      click: () => {
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);
      },
    },
    hour: {
      text: 'Óra',
      click: () => {
        setTimeInterval(slotIntervals.hour);
        setCalendarViews(calendarHourViews);
      },
    },
    day: {
      text: 'Nap',
      click: () => {
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);
      },
    },
    week: {
      text: 'Hét',
      click: () => {
        setTimeInterval(slotIntervals.week);
        setCalendarViews(calendarWeekViews);
      },
    },
    month: {
      text: 'Hónap',
      click: () => {
        setTimeInterval(slotIntervals.month);
        setCalendarViews(calendarMonthViews);
      },
    },
  };

  const generateButtons = (viewName) => {
    let retVal = {};
    switch (viewName) {
      case 'resourceTimelineDay':
        retVal = 'halfHour,hour,day';
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);

        break;
      case 'resourceTimelineWeek':
        retVal = 'halfHour,hour,day,week';
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);

        break;
      case 'resourceTimelineMonth':
        retVal = 'hour,day,week,month';
        setTimeInterval(slotIntervals.hour);
        setCalendarViews(calendarHourViews);

        break;
      case 'resourceTimelineYear':
        retVal = 'day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
    }
    return retVal;
  };

  const filteredResources = useMemo(() => {
    if (!relatedMachines || !filterFormState.productionId) {
      return tasks.resources;
    }

    return tasks.resources
      .map((group) => ({
        ...group,
        children: group.children?.filter((machine) => machine.productionIds?.includes(filterFormState.productionId)),
      }))
      .filter((group) => group.children?.length > 0);
  }, [tasks.resources, relatedMachines, filterFormState.productionId]);

  const activeTimeRange = useMemo(() => {
    if (!ganttFilter?.activeTimeRange) {
      return {
        slotMinTime: '00:00:00',
        slotMaxTime: '24:00:00',
      };
    }

    const validBackgroundEvents = tasks.backgroundEvents.filter((event) => event?.start && event?.end);

    if (!validBackgroundEvents.length) {
      return {
        slotMinTime: '00:00:00',
        slotMaxTime: '24:00:00',
      };
    }

    const minStart = validBackgroundEvents.reduce((earliest, event) => {
      const eventStart = dayjs(event.start);
      return eventStart.isBefore(earliest) ? eventStart : earliest;
    }, dayjs(validBackgroundEvents[0].start));

    const maxEnd = validBackgroundEvents.reduce((latest, event) => {
      const eventEnd = dayjs(event.end);
      return eventEnd.isAfter(latest) ? eventEnd : latest;
    }, dayjs(validBackgroundEvents[0].end));

    return {
      slotMinTime: minStart.format('HH:mm:ss'),
      slotMaxTime: maxEnd.format('HH:mm:ss'),
    };
  }, [ganttFilter?.activeTimeRange, tasks.backgroundEvents]);

  const activeStoreResource = useMemo(() => filteredResources
    .flatMap((group) => group.children || [])
    .find((resource) => resource.id === activeStoreResourceId), [filteredResources, activeStoreResourceId]);

  const storeFilters = useMemo(() => ({
    ...filterFormState,
    resourceOneId: activeStoreResource ? Number(activeStoreResource.id.substring(1)) : null,
  }), [filterFormState, activeStoreResource]);

  const selectStoreResource = (resource) => {
    setActiveStoreResourceId(resource.id);
    setFloatingStoreOpen(true);
    saveView('productionFloatingStore', { resourceId: resource.id, open: true }).then((saved) => {
      if (saved === false) addNotification({ content: 'A gyártástár ablakállapotának mentése sikertelen.', type: NotificationType.ERROR });
    });
  };

  return (
    <div className="machineCalendar taskListCalendar">
      <div ref={checkboxFiltersRef}>
        <GanttFilter
          filter={{ filterFormState, setFilterFormState }}
          ganttFilter={ganttFilter}
          setSimpleTaskDialogOpen={setSimpleTaskDialogOpen}
          setCreateProductDialogOpen={setCreateProductDialogOpen}
          setProductDrawerOpen={setProductDrawerOpen}
          canEdit={canEdit}
          relatedMachineState={{ relatedMachines, setRelatedMachines }}
        />
      </div>
      <FullCalendar
        ref={calendarRef}
        customButtons={timeIntervalButtons}
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        initialView="resourceTimelineDay"
        resources={filteredResources}
        resourceOrder="visOrder"
        locales={[huLocale]}
        locale="hu"
        slotLabelFormat={[
          { month: 'long', year: 'numeric', weekday: 'long' },
          { hour: 'numeric', minute: '2-digit' },
        ]}
        datesSet={handleDatesSet}
        events={[...tasks.taskEvents, ...tasks.backgroundEvents, ...tasks.prodEvents]}
        editable={canEdit}
        droppable={floatingStoreEnabled && canEdit && !storeSaving}
        eventReceive={receiveStoreOperation}
        eventAllow={(dropInfo, draggedEvent) => !draggedEvent?.extendedProps?.fromProductionStore || (
          canEdit && !storeSaving && dropInfo.resource?.id.startsWith('M')
        )}
        nowIndicator={true}
        slotMinTime={activeTimeRange.slotMinTime}
        slotMaxTime={activeTimeRange.slotMaxTime}
        eventResize={updateEvent}
        eventDurationEditable={params.PROD_GANTT_CALCULATE_ENDDATE === 'N'}
        eventDrop={updateEvent}
        eventClick={(clickInfo) => {
          if (clickInfo.event.display !== 'background') {
            const id = clickInfo.event.id;
            const productId = clickInfo.event.extendedProps.eventData.id;
            const eventType = clickInfo.event._def.extendedProps.eventData.type;
            if (clickInfo.jsEvent.ctrlKey) {
              const id = parseInt(clickInfo.event.id);
              setSelectedEvents((prev) => {
                const isSelected = prev.id.includes(id);
                const updatedIds = isSelected ? prev.id.filter((item) => item !== id) : [...prev.id, id];
                const newEvents = { ...prev, id: updatedIds };
                clickInfo.event.setProp(
                  'backgroundColor',
                  isSelected
                    ? getColorStyles(clickInfo.event.extendedProps?.eventData, parsedParam[0], 'PRODUCTION')
                        .backgroundColor
                    : 'rgba(126, 120, 210, 0.5)'
                );

                return newEvents;
              });
            } else if (eventType === 'PRODUCTION') {
              getProductById(productId);
            } else {
              getTaskById(id);
            }
          }
        }}
        eventContent={(eventInfo) => {
          if (eventInfo.event.display !== 'background') {
            return <EventChip eventInfo={eventInfo} />;
          }
        }}
        headerToolbar={{
          left: `prev,next,today ${intervalButtons}`,
          center: 'title',
          right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimelineYear',
        }}
        views={calendarViews}
        slotDuration={timeInterval}
        resourceAreaWidth="15%"
        resourceAreaHeaderContent="Erőforrás"
        resourceLabelContent={floatingStoreEnabled ? ({ resource }) => resource.id.startsWith('M') ? (
          <button type="button" className="production-store-resource-button"
            aria-pressed={resource.id === activeStoreResource?.id}
            title="Erőforrás gyártástárának megnyitása"
            onClick={() => selectStoreResource(resource)}>{resource.title}</button>
        ) : resource.title : undefined}
        resourceLaneClassNames={({ resource }) => floatingStoreEnabled && resource.id === activeStoreResource?.id
          ? ['production-store-active-resource'] : []}
        scrollTimeReset={false}
        resourceLabelClassNames={(data) => {
          if (floatingStoreEnabled && data.resource.id === activeStoreResource?.id) {
            return ['production-store-active-resource'];
          }
          if (!data.resource._resource.parentId) {
            return ['fc-res-group'];
          }
        }}
      />
      <SimpleTaskDialog
        open={dialogOpen}
        changed={{ taskChanged, setTaskChanged }}
        task={task}
        handleClose={() => {
          setDialogOpen(false);
        }}
      />

      <ProductDialog
        open={productDialogOpen}
        production={production}
        changed={{ productionChanged, setProductionChanged }}
        handleClose={() => {
          setProductDialogOpen(false);
        }}
      />
      <ProductDialog
        open={createProductDialogOpen}
        changed={{ productionChanged, setProductionChanged }}
        handleClose={() => {
          setCreateProductDialogOpen(false);
        }}
      />
      <SimpleTaskDialog
        changed={{ taskChanged, setTaskChanged }}
        open={simpleTaskDialogOpen}
        handleClose={() => {
          setSimpleTaskDialogOpen(false);
        }}
      />

      {floatingStoreEnabled && floatingStoreOpen && user.userId && (
        <FloatingProductionStore
          user={user}
          canEdit={canEdit && !storeSaving}
          filters={storeFilters}
          activeResource={activeStoreResource}
          revision={storeRevision}
          onClose={() => setStoreOpen(false)}
          onOpenProduction={getProductById}
          params={params}
          getColorStyles={(event) => getColorStyles(event, parsedParam[0], 'PRODUCTION')}
        />
      )}
      <DrawerUI open={productDrawerOpen} onClose={() => setProductDrawerOpen(false)} anchor="right" width="60vw">
        <ProductionStore user={user} refetch={{ productionChanged, setProductionChanged }} canEdit={canEdit} />
      </DrawerUI>
    </div>
  );
};
